/**
 * Program (relic) engine — fires data-driven triggers at well-defined points in
 * the combat loop and interprets their effects.
 *
 * Purity contract matches combat.ts: these functions mutate the RunState passed
 * in (the api layer deep-clones first). All randomness flows through the Rng.
 * Nothing here touches the DOM/time.
 *
 * To avoid a circular import with combat.ts (which owns damage/draw/trace
 * primitives), the combat loop passes those primitives in as `deps`. Programs
 * stay declarative data; this module is the pure interpreter.
 */

import { Rng } from "./rng";
import { addStatus, getStacks } from "./status";
import { getProgramDef } from "./content/programs";
import type {
  CardType,
  CombatEvent,
  IceInstance,
  ProgramEffect,
  ProgramHook,
  ProgramTrigger,
  RunState,
} from "./types";

export const RUNNER_UID = -1;

/**
 * Combat primitives the program interpreter needs. combat.ts supplies these so
 * we never import combat.ts here (no cycle).
 */
export interface ProgramDeps {
  dealToIce: (run: RunState, ice: IceInstance, raw: number, events: CombatEvent[]) => void;
  dealToRunner: (run: RunState, raw: number, unblockable: boolean, events: CombatEvent[]) => void;
  drawCards: (run: RunState, n: number, rng: Rng, events: CombatEvent[]) => void;
  pushTrace: (run: RunState, delta: number, reason: string, events: CombatEvent[]) => void;
}

/**
 * Context passed into a hook. Optional fields are populated only for the hooks
 * that have them (e.g. cardType/cardTags for cardPlayed, amount for blockGained).
 */
export interface HookContext {
  cardType?: CardType;
  cardTags?: string[];
  amount?: number;
  /** A target ICE relevant to the hook (e.g. the enemy just killed/hit). */
  target?: IceInstance;
}

/**
 * Fire a program hook. Iterates owned Programs in a STABLE order (run.programs
 * order) so behavior is deterministic. Mutates run; pushes `program` + effect
 * events. Combat-dependent effects no-op safely outside combat.
 */
export function fireHook(
  run: RunState,
  hook: ProgramHook,
  ctx: HookContext,
  rng: Rng,
  deps: ProgramDeps,
  events: CombatEvent[],
): void {
  if (run.programs.length === 0) return;
  for (const programId of run.programs) {
    const def = getProgramDef(programId);
    for (const trigger of def.triggers) {
      if (trigger.hook !== hook) continue;
      runTrigger(run, programId, trigger, ctx, rng, deps, events);
    }
  }
}

function runTrigger(
  run: RunState,
  programId: string,
  trigger: ProgramTrigger,
  ctx: HookContext,
  rng: Rng,
  deps: ProgramDeps,
  events: CombatEvent[],
): void {
  let fired = false;
  for (let i = 0; i < trigger.effects.length; i++) {
    const eff = trigger.effects[i]!;
    // Card-type / tag gating.
    if (eff.ifCardType && ctx.cardType !== eff.ifCardType) continue;
    if (eff.ifCardTag && !(ctx.cardTags ?? []).includes(eff.ifCardTag)) continue;

    // everyN gating: increment a per-effect counter keyed by program+hook+index.
    if (eff.everyN && eff.everyN > 1) {
      const combat = run.combat;
      if (!combat) continue;
      const key = `${programId}:${trigger.hook}:${i}`;
      const n = (combat.programCounters[key] ?? 0) + 1;
      combat.programCounters[key] = n;
      if (n % eff.everyN !== 0) continue;
    }

    applyProgramEffect(run, eff, ctx, rng, deps, events);
    fired = true;
  }
  if (fired) {
    events.push({ t: "program", programId, hook: trigger.hook });
  }
}

function applyProgramEffect(
  run: RunState,
  eff: ProgramEffect,
  ctx: HookContext,
  rng: Rng,
  deps: ProgramDeps,
  events: CombatEvent[],
): void {
  const combat = run.combat;
  const ctxScale = eff.scaleByCtxAmount ?? 0;
  const scaledValue =
    ctxScale > 0 ? Math.floor((ctx.amount ?? 0) * ctxScale) : eff.value ?? 0;
  const scaledAmount =
    ctxScale > 0 ? Math.floor((ctx.amount ?? 0) * ctxScale) : eff.amount ?? 0;

  switch (eff.kind) {
    case "gainStatusSelf": {
      if (!eff.status) break;
      run._runnerStatuses = addStatus(run._runnerStatuses, eff.status, scaledAmount);
      events.push({
        t: "status",
        targetUid: RUNNER_UID,
        status: eff.status,
        amount: scaledAmount,
      });
      break;
    }
    case "gainBlock": {
      const dex = getStacks(run._runnerStatuses, "dexterity");
      const v = scaledValue + dex;
      run._runnerStatuses = addStatus(run._runnerStatuses, "block", v);
      events.push({ t: "block", targetUid: RUNNER_UID, amount: v });
      break;
    }
    case "gainCycles": {
      if (combat) combat.cycles += scaledValue;
      break;
    }
    case "draw": {
      if (combat) deps.drawCards(run, scaledValue, rng, events);
      break;
    }
    case "dealDamageTarget": {
      if (!combat) break;
      const target = ctx.target ?? combat.enemies.find((e) => e.alive);
      if (target && target.alive) deps.dealToIce(run, target, scaledValue, events);
      break;
    }
    case "dealDamageAll": {
      if (!combat) break;
      for (const ice of combat.enemies) {
        if (ice.alive) deps.dealToIce(run, ice, scaledValue, events);
      }
      break;
    }
    case "applyStatusAll": {
      if (!combat || !eff.status) break;
      for (const ice of combat.enemies) {
        if (!ice.alive) continue;
        ice.statuses = addStatus(ice.statuses, eff.status, scaledAmount);
        events.push({
          t: "status",
          targetUid: ice.uid,
          status: eff.status,
          amount: scaledAmount,
        });
      }
      break;
    }
    case "heal": {
      run.hp = Math.min(run.maxHp, run.hp + scaledValue);
      break;
    }
    case "addTrace": {
      deps.pushTrace(run, scaledValue, "program", events);
      break;
    }
    case "reduceTrace": {
      deps.pushTrace(run, -scaledValue, "program", events);
      break;
    }
    case "loseHpSelf": {
      deps.dealToRunner(run, scaledValue, true, events);
      break;
    }
    default: {
      const _exhaustive: never = eff.kind;
      throw new Error(`Unhandled program effect ${_exhaustive}`);
    }
  }
}

/**
 * Aggregate the static `mods` of all owned Programs (cycles/handSize/etc).
 * Pure; used by combat setup. Returns summed deltas + merged innate status.
 */
export function aggregateProgramMods(run: RunState): {
  cyclesPerTurn: number;
  handSize: number;
  startingBlock: number;
  traceMaxDelta: number;
  tracePerTurnDelta: number;
  innateStatus: Record<string, number>;
} {
  const out = {
    cyclesPerTurn: 0,
    handSize: 0,
    startingBlock: 0,
    traceMaxDelta: 0,
    tracePerTurnDelta: 0,
    innateStatus: {} as Record<string, number>,
  };
  for (const id of run.programs) {
    const mods = getProgramDef(id).mods;
    if (!mods) continue;
    out.cyclesPerTurn += mods.cyclesPerTurn ?? 0;
    out.handSize += mods.handSize ?? 0;
    out.startingBlock += mods.startingBlock ?? 0;
    out.traceMaxDelta += mods.traceMaxDelta ?? 0;
    out.tracePerTurnDelta += mods.tracePerTurnDelta ?? 0;
    if (mods.innateStatus) {
      for (const [k, v] of Object.entries(mods.innateStatus)) {
        out.innateStatus[k] = (out.innateStatus[k] ?? 0) + (v ?? 0);
      }
    }
  }
  return out;
}
